import React, { useContext, useEffect, useState } from "react";
import { Container, Row, Col, Card, Badge, Spinner, Alert, ListGroup, Button, Modal, Form } from "react-bootstrap";
import styled from "styled-components";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import { Skeleton } from "@mui/material";
import { useActionsContext } from "../../context/ActionsContext";
import { AuthContext } from "../../context/AuthContext";
import axios from "axios";
import SecurityHeaders from "../../context/SecurityHeaders";
import ENDPOINT from "../../context/EndPoint";

// --- Styled Components (No Changes) ---
const SkillBadge = styled(Badge)`
  background: #e3f2fd !important;
  color: #1976d2 !important;
  margin-right: 6px;
  margin-bottom: 4px;
  font-size: 0.95em;
`;

const SectionTitle = styled.h5`
  color: #333;
  border-left: 4px solid #0d6efd;
  padding-left: 10px;
  margin-top: 1.5rem;
  margin-bottom: 1rem;
`;

const ChallengeCard = styled(Card)`
  margin-bottom: 2rem;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.07);
  border-radius: 14px;
`;

// --- Skeleton Loader Component (No Changes) ---
const ChallengeSkeleton = () => (
    <Col xs={12}>
        <ChallengeCard>
            <Card.Body>
                <Skeleton variant="text" width="60%" height={40} />
                <Skeleton variant="text" width="40%" height={20} />
                <Skeleton variant="text" width="30%" height={20} className="mb-3" />
                <Skeleton variant="rectangular" height={40} className="mb-3" />

                <SectionTitle style={{ border: 'none', paddingLeft: 0 }}>
                    <Skeleton variant="text" width="35%" height={30} />
                </SectionTitle>
                <div className="mb-3 d-flex flex-wrap">
                    <Skeleton variant="rounded" width={80} height={32} className="me-2 mb-2" />
                    <Skeleton variant="rounded" width={100} height={32} className="me-2 mb-2" />
                    <Skeleton variant="rounded" width={70} height={32} className="mb-2" />
                </div>

                <SectionTitle style={{ border: 'none', paddingLeft: 0 }}>
                    <Skeleton variant="text" width="30%" height={30} />
                </SectionTitle>
                <Skeleton variant="rectangular" height={50} />
            </Card.Body>
            <Card.Footer className="text-muted small d-flex justify-content-between align-items-center">
                <Skeleton variant="text" width="40%" />
                <div className="d-flex">
                    <Skeleton variant="rounded" width={60} height={30} className="me-2" />
                    <Skeleton variant="rounded" width={70} height={30} />
                </div>
            </Card.Footer>
        </ChallengeCard>
    </Col>
);

const HackingChallengesAssignments = () => {
  const { batch_id, course_url } = useParams();
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Edit modal state
  const [showEdit, setShowEdit] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  // Add modal state
  const [showAdd, setShowAdd] = useState(false);
  const [addLoading, setAddLoading] = useState(false);

  // Delete modal state
  const [showDelete, setShowDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Instructions and Submission Process state
  const [instructions, setInstructions] = useState([""]);
  const [submissionProcess, setSubmissionProcess] = useState([""]);

  const { rowsPerPage } = useActionsContext();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  // Fetch challenges
  const fetchChallenges = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('session', user.SessionId);
      formData.append('limit', rowsPerPage);
      formData.append('batch_id', batch_id);
      formData.append('course_url', course_url);

      const res = await axios.post(ENDPOINT+ "get-hacking-challenges.php", formData, SecurityHeaders);

      const data = res.data;
      if (data.status === "success" || data.status === "no_data") {
        const parsed = data.data.map((challenge) => ({
          ...challenge,
          skills_required: Array.isArray(challenge.skills_required)
            ? challenge.skills_required
            : (challenge.skills_required ? JSON.parse(challenge.skills_required) : []),
          instructions: Array.isArray(challenge.instructions)
            ? challenge.instructions
            : (challenge.instructions ? JSON.parse(challenge.instructions) : []),
          submission_process: Array.isArray(challenge.submission_process)
            ? challenge.submission_process
            : (challenge.submission_process ? JSON.parse(challenge.submission_process) : []),
        }));
        setChallenges(parsed);
      } else {
        setError(data.message || "Failed to load challenges.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChallenges();
  }, [rowsPerPage]);

  // Handle Edit
  const handleEdit = (challenge) => {
    setEditData({
      ...challenge,
      skills_required: (challenge.skills_required || []).join(", "),
      instructions: (challenge.instructions || []).join("\n"),
      submission_process: (challenge.submission_process || []).join("\n"),
    });
    setShowEdit(true);
  };

  const handleEditChange = (e) => {
    setEditData({ ...editData, [e.target.name]: e.target.value });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    const formData = new FormData();
    formData.append('session', user.SessionId);
    formData.append("challenge_id", editData.challenge_id);
    formData.append("title", editData.title);
    formData.append("description", editData.description);
    formData.append("prize_money", editData.prize_money);
    formData.append("submission_deadline", editData.submission_deadline);
    formData.append("skills_required", JSON.stringify(editData.skills_required.split(",").map((s) => s.trim())));
    formData.append("instructions", JSON.stringify(instructions));
    formData.append("submission_process", JSON.stringify(submissionProcess));

    try {
      const res = await fetch(ENDPOINT +"update-hacking-challenges.php", {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (data.status === "success") {
        setShowEdit(false);
        fetchChallenges();
      } else {
        alert(data.message || "Failed to update challenge.");
      }
    } catch {
      alert("Network error.");
    }
    setEditLoading(false);
  };

  // Handle Add
  const handleAdd = () => {
    setEditData({
      title: "",
      description: "",
      prize_money: "",
      submission_deadline: "",
      skills_required: "",
      instructions: "",
      submission_process: "",
    });
    setInstructions([""]);
    setSubmissionProcess([""]);
    setShowAdd(true);
  };

  const handleAddChange = (e) => {
    setEditData({ ...editData, [e.target.name]: e.target.value });
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    const formData = new FormData();
    formData.append('session', user.SessionId);
    formData.append("title", editData.title);
    formData.append("description", editData.description);
    formData.append("prize_money", editData.prize_money);
    formData.append("submission_deadline", editData.submission_deadline);
    formData.append("skills_required", JSON.stringify(editData.skills_required.split(",").map((s) => s.trim())));
    formData.append("instructions", JSON.stringify(instructions));
    formData.append("submission_process", JSON.stringify(submissionProcess));
    formData.append('batch_id', batch_id);
    formData.append('course_url', course_url);

    try {
      const res = await fetch(ENDPOINT+"add-hacking-challenge.php", {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (data.status === "success") {
        setShowAdd(false);
        fetchChallenges();
      } else {
        alert(data.message || "Failed to add challenge.");
      }
    } catch {
      alert("Network error.");
    }
    setAddLoading(false);
  };

  // Handle Delete
  const handleDelete = (challenge_id) => {
    setDeleteId(challenge_id);
    setShowDelete(true);
  };

  const confirmDelete = async () => {
    setDeleteLoading(true);
    const formData = new FormData();
    formData.append("challenge_id", deleteId);
    try {
      const res = await fetch(ENDPOINT + "delete-hacking-challenge.php", {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (data.status === "success") {
        setShowDelete(false);
        fetchChallenges();
      } else {
        alert(data.message || "Failed to delete challenge.");
      }
    } catch {
      alert("Network error.");
    }
    setDeleteLoading(false);
  };

  // For edit/add mode, initialize arrays from editData
  useEffect(() => {
    if ((showEdit || showAdd) && editData) {
      setInstructions(
        Array.isArray(editData.instructions)
          ? editData.instructions
          : (editData.instructions ? editData.instructions.split("\n") : [""])
      );
      setSubmissionProcess(
        Array.isArray(editData.submission_process)
          ? editData.submission_process
          : (editData.submission_process ? editData.submission_process.split("\n") : [""])
      );
    }
  }, [showEdit, showAdd, editData]);

  const handleDynamicChange = (index, value, type) => {
    if (type === "instructions") {
      const arr = [...instructions];
      arr[index] = value;
      setInstructions(arr);
    } else if (type === "submission") {
      const arr = [...submissionProcess];
      arr[index] = value;
      setSubmissionProcess(arr);
    }
  };

  const addDynamicField = (type) => {
    if (type === "instructions") {
      setInstructions([...instructions, ""]);
    } else if (type === "submission") {
      setSubmissionProcess([...submissionProcess, ""]);
    }
  };

  const removeDynamicField = (index, type) => {
    if (type === "instructions") {
      const arr = instructions.filter((_, i) => i !== index);
      setInstructions(arr.length ? arr : [""]);
    } else if (type === "submission") {
      const arr = submissionProcess.filter((_, i) => i !== index);
      setSubmissionProcess(arr.length ? arr : [""]);
    }
  };

  if (loading) {
    return (
      <Container className="py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <Skeleton variant="text" width={250} height={50} />
          <Skeleton variant="rounded" width={180} height={40} />
        </div>
        <Row>
          <ChallengeSkeleton />
          <ChallengeSkeleton />
          <ChallengeSkeleton />
        </Row>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <div className="py-3 mx-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4>Hacking Challenges</h4>
        {!error && (
          <div>
            <Button variant="primary" onClick={handleAdd}>
              Add New Challenge
            </Button>
          </div>
        )}
      </div>
      <Row>
        {challenges.map((challenge) => (
          <Col xs={12} key={challenge.challenge_id}>
            <ChallengeCard>
              <Card.Body>
                <Card.Subtitle className="mb-2">
                  Assignment id: <b>{challenge.challenge_id}</b>
                </Card.Subtitle>
                <Card.Title className="mb-2">{challenge.title}</Card.Title>
                <Card.Subtitle className="mb-2 text-muted">
                  Prize: <b>₹{challenge.prize_money}</b>
                </Card.Subtitle>
                <div className="mb-2">
                  <small className="text-muted">
                    Deadline: {dayjs(challenge.submission_deadline).format("DD MMM YYYY, HH:mm")}
                  </small>
                </div>
                <Card.Text className="mb-3" style={{ minHeight: 30 }}>
                  {challenge.description}
                </Card.Text>
                <SectionTitle>Skills Required</SectionTitle>
                <div className="mb-3">
                  {(challenge.skills_required || []).map((skill, idx) => (
                    <SkillBadge key={idx} pill>
                      {skill}
                    </SkillBadge>
                  ))}
                </div>
                <SectionTitle>Instructions</SectionTitle>
                <ListGroup variant="flush" className="mb-3">
                  {(challenge.instructions || []).map((inst, idx) => (
                    <ListGroup.Item key={idx}>{inst}</ListGroup.Item>
                  ))}
                </ListGroup>
                <SectionTitle>Submission Process</SectionTitle>
                <ListGroup variant="flush" className="mb-3">
                  {(challenge.submission_process || []).map((step, idx) => (
                    <ListGroup.Item key={idx}>{step}</ListGroup.Item>
                  ))}
                </ListGroup>
              </Card.Body>
              <Card.Footer className="text-muted small d-flex justify-content-between align-items-center">
                <span>
                  Created: {dayjs(challenge.created_at).format("DD MMM YYYY")}
                  {challenge.updated_at && <> | Updated: {dayjs(challenge.updated_at).format("DD MMM YYYY")}</>}
                </span>
                <span>
                  <Button size="sm" variant="outline-primary" className="me-2" onClick={() => handleEdit(challenge)}>
                    Edit
                  </Button>
                  <Button size="sm" variant="outline-danger" onClick={() => handleDelete(challenge.challenge_id)}>
                    Delete
                  </Button>
                  <Button variant="primary" onClick={() => navigate('/hacking-challenge-sub/'+challenge.challenge_id)} className="ms-3">
                    Submissions
                  </Button>
                </span>
              </Card.Footer>
            </ChallengeCard>
          </Col>
        ))}
      </Row>
      {challenges.length === 0 && <div className="text-center text-muted py-5">No challenges found.</div>}

      {/* Edit Modal */}
      <Modal show={showEdit} onHide={() => setShowEdit(false)} size="lg">
        <Form onSubmit={handleEditSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Edit Challenge</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {editData && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Title</Form.Label>
                  <Form.Control name="title" value={editData.title} onChange={handleEditChange} required />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control as="textarea" name="description" value={editData.description} onChange={handleEditChange} rows={3} required />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Prize Money</Form.Label>
                  <Form.Control name="prize_money" type="number" value={editData.prize_money} onChange={handleEditChange} required />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Submission Deadline</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    name="submission_deadline"
                    value={dayjs(editData.submission_deadline).format("YYYY-MM-DDTHH:mm")}
                    onChange={handleEditChange}
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Skills Required (comma separated)</Form.Label>
                  <Form.Control name="skills_required" value={editData.skills_required} onChange={handleEditChange} required />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Dynamic Instructions</Form.Label>
                  {instructions.map((inst, idx) => (
                    <div key={idx} className="d-flex mb-2">
                      <Form.Control
                        value={inst}
                        onChange={(e) => handleDynamicChange(idx, e.target.value, "instructions")}
                        placeholder={`Instruction ${idx + 1}`}
                        required
                      />
                      <Button
                        variant="outline-danger"
                        className="ms-2"
                        onClick={() => removeDynamicField(idx, "instructions")}
                        disabled={instructions.length === 1}
                      >
                        &times;
                      </Button>
                    </div>
                  ))}
                  <Button variant="outline-primary" onClick={() => addDynamicField("instructions")}>
                    Add Instruction
                  </Button>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Dynamic Submission Process</Form.Label>
                  {submissionProcess.map((step, idx) => (
                    <div key={idx} className="d-flex mb-2">
                      <Form.Control
                        value={step}
                        onChange={(e) => handleDynamicChange(idx, e.target.value, "submission")}
                        placeholder={`Step ${idx + 1}`}
                        required
                      />
                      <Button
                        variant="outline-danger"
                        className="ms-2"
                        onClick={() => removeDynamicField(idx, "submission")}
                        disabled={submissionProcess.length === 1}
                      >
                        &times;
                      </Button>
                    </div>
                  ))}
                  <Button variant="outline-primary" onClick={() => addDynamicField("submission")}>
                    Add Submission Step
                  </Button>
                </Form.Group>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowEdit(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={editLoading}>
              {editLoading ? "Saving..." : "Save Changes"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Add Modal */}
      <Modal show={showAdd} onHide={() => setShowAdd(false)} size="lg">
        <Form onSubmit={handleAddSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Add New Challenge</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control name="title" value={editData?.title || ""} onChange={handleAddChange} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control as="textarea" name="description" value={editData?.description || ""} onChange={handleAddChange} rows={3} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Prize Money</Form.Label>
              <Form.Control name="prize_money" type="number" value={editData?.prize_money || ""} onChange={handleAddChange} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Submission Deadline</Form.Label>
              <Form.Control
                type="datetime-local"
                name="submission_deadline"
                value={editData?.submission_deadline ? dayjs(editData.submission_deadline).format("YYYY-MM-DDTHH:mm") : ""}
                onChange={handleAddChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Skills Required (comma separated)</Form.Label>
              <Form.Control name="skills_required" value={editData?.skills_required || ""} onChange={handleAddChange} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Dynamic Instructions</Form.Label>
              {instructions.map((inst, idx) => (
                <div key={idx} className="d-flex mb-2">
                  <Form.Control
                    value={inst}
                    onChange={(e) => handleDynamicChange(idx, e.target.value, "instructions")}
                    placeholder={`Instruction ${idx + 1}`}
                    required
                  />
                  <Button
                    variant="outline-danger"
                    className="ms-2"
                    onClick={() => removeDynamicField(idx, "instructions")}
                    disabled={instructions.length === 1}
                  >
                    &times;
                  </Button>
                </div>
              ))}
              <Button variant="outline-primary" onClick={() => addDynamicField("instructions")}>
                Add Instruction
              </Button>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Dynamic Submission Process</Form.Label>
              {submissionProcess.map((step, idx) => (
                <div key={idx} className="d-flex mb-2">
                  <Form.Control
                    value={step}
                    onChange={(e) => handleDynamicChange(idx, e.target.value, "submission")}
                    placeholder={`Step ${idx + 1}`}
                    required
                  />
                  <Button
                    variant="outline-danger"
                    className="ms-2"
                    onClick={() => removeDynamicField(idx, "submission")}
                    disabled={submissionProcess.length === 1}
                  >
                    &times;
                  </Button>
                </div>
              ))}
              <Button variant="outline-primary" onClick={() => addDynamicField("submission")}>
                Add Submission Step
              </Button>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAdd(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={addLoading}>
              {addLoading ? "Adding..." : "Add Challenge"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Modal */}
      <Modal show={showDelete} onHide={() => setShowDelete(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Challenge</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this challenge?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDelete(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={deleteLoading}>
            {deleteLoading ? "Deleting..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default HackingChallengesAssignments;